import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Lock, LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminLogin = () => {
  const { isAdmin, login, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ username: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (loading) return null;
  if (isAdmin) {
    const to = location.state?.from || '/admin';
    return <Navigate to={to} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(form.username, form.password);
      nav(location.state?.from || '/admin', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center mx-auto mb-4">
            <Lock size={22} />
          </div>
          <h1 className="font-display text-4xl text-[#2a1a45]">Parish Admin</h1>
          <p className="text-sm text-[#5a534a] mt-2">Sign in to manage parish content.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[#e9e0cc] p-8 space-y-5">
          {error && (
            <div className="flex items-start gap-2 text-sm text-[#7a1c1c] bg-[#fdecec] border border-[#f0c4c4] p-3">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Username</label>
            <input
              autoFocus
              data-testid="login-username-input"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              required
              autoComplete="username"
              className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Password</label>
            <input
              type="password"
              data-testid="login-password-input"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
            />
          </div>
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#3a2359] text-white text-sm tracking-wide hover:bg-[#4d2f75] disabled:opacity-60"
          >
            <LogIn size={16} /> {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="text-xs text-[#78716c] text-center">For authorized parish staff only.</p>
        </form>
      </div>
    </section>
  );
};

export default AdminLogin;
